import { createResource } from '@/store/utils/resource'
import { createAsyncType } from '@/store/utils/mutation-types'
import axios from 'axios'
import teamsByQuestionnaire from './teams-by-questionnaire'
import usersByTeam from './users-by-team'
import get from 'lodash/get'
import findIndex from 'lodash/findIndex'
import { colors } from '../../../../util/colors.js'
import { createResource as createMonthFilterResource } from '@/store/utils/month-filter-resource'

const monthFilterResource = createMonthFilterResource()

const types = {
  WILL_PREFETCH: 'WILL_PREFETCH',
  DID_PREFETCH: 'DID_PREFETCH',
  GET_WAVELINE_OVERVIEW: createAsyncType('GET_WAVELINE_OVERVIEW'),
  SET_WAVELINE_NOTE: createAsyncType('SET_WAVELINE_NOTE')
}

export default {
  namespaced: true,
  state () {
    return {
      prefetching: false,
      prefetched: false,
      directUser: null,
      directTeam: null,
      directQuestionnaire: null,
      wavelineOverview: null,
      wavelineOverviewStatus: void 0,
      queryId: null,
      statusNote: void 0,
      ...monthFilterResource.state
    }
  },
  actions: {

    async setNote ({commit}, { questionnaireId, teamId, userId = null, queryId, item }) {
      commit(types.SET_WAVELINE_NOTE.REQUEST, {queryId})
      try {
        if (userId) {
          commit(types.SET_WAVELINE_NOTE.SUCCESS, await axios.put(`/api/waveline/questionnaires/${questionnaireId}/team/${teamId}/user/${userId}/note/${queryId}`, item))
        } else {
          commit(types.SET_WAVELINE_NOTE.SUCCESS, await axios.put(`/api/waveline/questionnaires/${questionnaireId}/team/${teamId}/note/${queryId}`, item))
        }
      } catch (e) {
        commit(types.SET_WAVELINE_NOTE.FAILURE)
      }
    },

    async getWavelineOverview ({ commit }, { questionnaireId, teamId, userId = null, months }) {
      commit(types.GET_WAVELINE_OVERVIEW.REQUEST, { teamId, questionnaireId })
      try {
        if (userId) {
          commit(types.GET_WAVELINE_OVERVIEW.SUCCESS, await axios.get(`/api/waveline/questionnaires/${questionnaireId}/team/${teamId}/user/${userId}`, { params: { months } }))
        } else {
          commit(types.GET_WAVELINE_OVERVIEW.SUCCESS, await axios.get(`/api/waveline/questionnaires/${questionnaireId}/team/${teamId}`, { params: { months } }))
        }
      } catch (e) {
        commit(types.GET_WAVELINE_OVERVIEW.FAILURE)
      }
    },

    async prefetch (context, { refresh } = {}) {
      const { commit, dispatch, state } = context

      if (!state.prefetched || refresh) {
        commit(types.WILL_PREFETCH)

        await dispatch('questionnaires/getList', { perPage: 10 })
        const prefetch = { flag: true }

        if (state.questionnaires.items.length === 1) {
          prefetch.directQuestionnaire = state.questionnaires.items[0]
        }
        commit(types.DID_PREFETCH, prefetch)
      }
    },

    ...monthFilterResource.actions

  },
  mutations: {
    [types.WILL_PREFETCH]: (state) => {
      state.prefetching = true
      state.prefetched = false
      state.directTeam = null
      state.directQuestionnaire = null
    },
    [types.DID_PREFETCH]: (state, { flag, directQuestionnaire, directTeam }) => {
      state.prefetching = false
      state.prefetched = flag
      state.directQuestionnaire = directQuestionnaire
      state.directTeam = directTeam
    },
    [types.GET_WAVELINE_OVERVIEW.REQUEST]: (state) => {
      state.wavelineOverviewStatus = 'fetching'
      state.wavelineOverview = {}
    },
    [types.GET_WAVELINE_OVERVIEW.SUCCESS]: (state, result) => {
      state.wavelineOverviewStatus = 'success'
      state.wavelineOverview = result.data
    },
    [types.GET_WAVELINE_OVERVIEW.FAILURE]: state => {
      state.wavelineOverviewStatus = 'failure'
      state.wavelineOverview = {}
    },
    [types.SET_WAVELINE_NOTE.REQUEST]: (state, { queryId }) => {
      state.statusNote = 'fetching'
      state.queryId = queryId
    },
    [types.SET_WAVELINE_NOTE.SUCCESS]: (state, result) => {
      const index = findIndex(state.wavelineOverview.tasks, task => task && task.query === state.queryId)
      if (index !== -1) {
        state.wavelineOverview.tasks[index].note = result.data.note
      }
      state.statusNote = 'success'
    },
    [types.SET_WAVELINE_NOTE.FAILURE]: state => {
      state.statusNote = 'failure'
    },
    ...monthFilterResource.mutations
  },
  modules: {
    questionnaires: createResource('waveline/questionnaires', { perPage: 10 }),
    teamsByQuestionnaire,
    usersByTeam
  },
  getters: {
    wavelineData: state => {
      const data = get(state.wavelineOverview, 'wavelineData', null)
      if (data) {
          data.labels.unshift('')
          const nullArray = []
          if (data.values.length > 0) {
            for (let i = 0; i < data.values[0].length; i++) {
              nullArray.push(null)
            }
          }
          data.values.unshift(nullArray)
      }
      return data
    },
    tasks: state => {
      const tasks = get(state.wavelineOverview, 'tasks', [])
      if (tasks.length > 0) {
        tasks.unshift(null)
      }
      return tasks
    },
    teamDiff: state => {
      const datasets = get(state.wavelineOverview, 'teamDiff', []).map((data, index) => {
        let props = {borderColor: colors[1], backgroundColor: 'rgba(0, 0, 0, 0)', borderWidth: 2, pointRadius: 0, pointHoverRadius: 0}
        if (index === 0) {
          props.borderColor = colors[8]
          props.borderWidth = 8
        }
        return Object.assign(data, props)
      })
      
      let anyAlertActive = 0
      const alertData = get(state.wavelineOverview, 'alertData', null)
      if (alertData) {
        datasets.push({
          data: alertData.alertChart.upperValues,
          valueColorGradient: false,
          borderWidth: 0,
          pointRadius: 0,
          pointHoverRadius: 0,
          borderColor: 'rgba(0, 142, 165, 0.05)',
          backgroundColor: 'rgba(0, 142, 165, 0.05)',
          fill: 'end'
        })

        datasets.push({
          data: alertData.alertChart.lowerValues,
          valueColorGradient: false,
          borderWidth: 0,
          pointRadius: 0,
          pointHoverRadius: 0,
          borderColor: 'rgba(0, 142, 165, 0.05)',
          backgroundColor: 'rgba(0, 142, 165, 0.05)',
          fill: 'start'
        })

        anyAlertActive = alertData.alertActive
      }
      return { labels: get(state.wavelineOverview, 'teamDiffLabels', []), datasets, anyAlertActive }
    }
  }
}
